import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { ChevronLeft, Star, MapPin, Clock, Phone, Heart, Share2, Crown, Lock } from "lucide-react";
import { formatSpotLocation } from "../lib/format-spot-location";
import { getCDNImage } from "../lib/cdnImage";
import { getFallbackSpotById } from "../lib/fallback-spots";

const SPOT_DETAIL_FALLBACK =
  "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1920&q=80";

function SpotDetailPage() {
  const { spotId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [spot, setSpot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);

  useEffect(() => {
    const fetchSpot = async () => {
      try {
        const response = await axios.get(`${API}/spots/${spotId}`, { withCredentials: true });
        setSpot(response.data);
      } catch (error) {
        const fallbackSpot = getFallbackSpotById(spotId);
        if (fallbackSpot) {
          setSpot(fallbackSpot);
        } else {
          toast.error("Failed to load spot");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchSpot();
    window.scrollTo(0, 0);
  }, [spotId]);

  const toggleFavorite = async () => {
    if (!user) { navigate("/auth"); return; }
    try {
      if (isFavorite) {
        await axios.delete(`${API}/users/favorites/${spotId}`, { withCredentials: true });
        setIsFavorite(false);
        toast.success("Removed from favorites");
      } else {
        await axios.post(`${API}/users/favorites/${spotId}`, {}, { withCredentials: true });
        setIsFavorite(true);
        toast.success("Added to favorites");
      }
    } catch {
      toast.error("Could not update favorites");
    }
  };

  useEffect(() => {
    if (!user || !spotId) return;
    axios.get(`${API}/users/favorites`, { withCredentials: true }).then((res) => {
      const ids = (res.data?.favorites || []).map((s) => s.spot_id);
      setIsFavorite(ids.includes(spotId));
    }).catch(() => {});
  }, [user, spotId]);

  const handleShare = async () => {
    try { await navigator.share({ title: spot?.name, url: window.location.href }); } 
    catch { navigator.clipboard.writeText(window.location.href); toast.success("Link copied"); }
  };

  const isPro = user?.tier === "pro" || user?.subscription_tier === "premium" || user?.is_pro;
  const isLocked = spot?.locked && !isPro;

  if (loading) {
    return (<div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" /></div>);
  }

  if (!spot) {
    return (<div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="text-center"><h2 className="text-2xl font-bold mb-4">Spot not found</h2><Link to="/discover"><Button className="rounded-full">Back to Discovery</Button></Link></div></div>);
  }

  const locationText = formatSpotLocation(spot);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-700 hover:text-gray-900"><ChevronLeft className="w-5 h-5" /><span className="font-medium">Back</span></button>
          <div className="flex items-center gap-2">
            <button onClick={handleShare} className="p-3 rounded-full hover:bg-gray-100"><Share2 className="w-5 h-5" /></button>
            <button onClick={toggleFavorite} className={`p-3 rounded-full ${isFavorite ? "bg-rose-100 text-rose-500" : "hover:bg-gray-100"}`}><Heart className={`w-5 h-5 ${isFavorite ? "fill-current" : ""}`} /></button>
          </div>
        </div>
      </header>

      <section className="relative pt-16 h-[60vh] min-h-[400px]">
        <img src={getCDNImage(spot.image_url || (spot.images && spot.images[0]), SPOT_DETAIL_FALLBACK)} alt={spot.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        <div className="absolute top-24 left-6 flex gap-2">
          {spot.is_featured && <Badge className="bg-amber-500 text-white border-0 shadow-lg">Featured</Badge>}
          {spot.is_premium_content && <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0 shadow-lg"><Crown className="w-3 h-3 mr-1" /> Premium</Badge>}
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 text-white/80 mb-3"><MapPin className="w-5 h-5" /><span>{locationText}</span>{spot.rating && <span className="flex items-center gap-1 ml-2"><Star className="w-5 h-5 text-amber-400 fill-amber-400" /><span className="font-semibold">{spot.rating}</span></span>}</div>
            <h1 className="text-3xl md:text-5xl font-serif font-bold text-white mb-4">{spot.name}</h1>
          </div>
        </div>
      </section>

      <section className="py-12 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          {isLocked && (
            <div className="mb-8 p-8 rounded-3xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 text-center">
              <Lock className="w-12 h-12 mx-auto mb-4 text-amber-500" />
              <h3 className="text-xl font-bold mb-2">Premium Content</h3>
              <p className="text-gray-600 mb-6">Upgrade to PRO to unlock full details, reviews, and exclusive tips.</p>
              <Link to="/premium"><Button className="rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white px-8"><Crown className="w-4 h-4 mr-2" /> Unlock with PRO</Button></Link>
            </div>
          )}
          <div className="mb-12">
            <h2 className="text-2xl font-serif font-bold mb-4">About</h2>
            <p className={`text-gray-700 leading-relaxed text-lg ${isLocked ? "line-clamp-3" : ""}`}>{spot.description}</p>
          </div>
          <div className="grid sm:grid-cols-3 gap-4 mb-12">
            {spot.opening_hours && <div className="p-5 rounded-2xl bg-white border border-gray-100 shadow-sm"><Clock className="w-6 h-6 text-cyan-500 mb-3" /><div className="text-sm text-gray-500 mb-1">Hours</div><div className="font-semibold">{spot.opening_hours}</div></div>}
            {spot.phone && <div className="p-5 rounded-2xl bg-white border border-gray-100 shadow-sm"><Phone className="w-6 h-6 text-cyan-500 mb-3" /><div className="text-sm text-gray-500 mb-1">Contact</div><div className="font-semibold">{spot.phone}</div></div>}
          </div>
          <div className="flex flex-col sm:flex-row gap-4 mb-12">
            <Button onClick={toggleFavorite} variant={isFavorite ? "default" : "outline"} className="flex-1 rounded-full py-6"><Heart className={`w-5 h-5 mr-2 ${isFavorite ? "fill-current" : ""}`} />{isFavorite ? "Saved" : "Save to Favorites"}</Button>
            <Button onClick={handleShare} variant="outline" className="flex-1 rounded-full py-6"><Share2 className="w-5 h-5 mr-2" /> Share</Button>
          </div>
        </div>
      </section>

      {!isPro && (
        <section className="py-12 px-4 md:px-8">
          <div className="max-w-4xl mx-auto p-8 md:p-12 rounded-3xl bg-gradient-to-br from-cyan-500 to-blue-600 text-white text-center">
            <Crown className="w-12 h-12 mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-serif font-bold mb-3">Unlock All Premium Content</h2>
            <p className="text-white/90 mb-6">Get unlimited access to hidden gems, insider tips, and exclusive spots across the Caribbean.</p>
            <Link to="/premium"><Button className="rounded-full px-8 py-6 bg-white text-cyan-600 font-semibold hover:bg-gray-50">Start Free Trial</Button></Link>
          </div>
        </section>
      )}
    </div>
  );
}

export default SpotDetailPage;
